import { useEffect, useState } from "react";
import { api } from "../api/http.js";
import { useSession } from "../state/session.js";
import { Button } from "../ui/Button.js";
import { AccountsPanel } from "./AccountsPanel.js";
import { LiveOpsPanel } from "./LiveOpsPanel.js";
import { OperationsPanel } from "./OperationsPanel.js";
import { AlliancesPanel } from "./AlliancesPanel.js";
import type { LogEntry, Operations, Overview } from "./types.js";
import { stamp, useAdminText } from "./text.js";
import { useAdminAction } from "./useAdminAction.js";
import "./admin.css";

function AuditPanel() {
  const t = useAdminText();
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const action = useAdminAction();
  const refresh = async () => { setEntries((await api<{ entries: LogEntry[] }>("GET", "/api/admin/log")).entries); };
  useEffect(() => { void action.run(refresh); }, []);
  return <section className="gm-card"><div className="gm-heading"><h2>{t("log")}</h2><Button loading={action.pending} onClick={() => void action.run(refresh)}>{t("refresh")}</Button></div><p>{t("auditHelp")}</p>
    <div className="gm-list">{entries.map((entry) => <article key={entry.id} className="gm-backup"><h3>{entry.action}</h3><p>{stamp(entry.createdAt)} · {t("actor")}: <code>{entry.adminId}</code></p><details><summary>{t("details")}</summary><pre>{JSON.stringify(entry.payload, null, 2)}</pre></details></article>)}</div>
    {action.error && <p role="alert" className="gm-error">{action.error}</p>}
  </section>;
}

function AuthorizedConsole() {
  const t = useAdminText();
  const [tab, setTab] = useState<"operations" | "accounts" | "alliances" | "events" | "log">("operations");
  const [operations, setOperations] = useState<Operations | null>(null);
  const [overview, setOverview] = useState<Overview | null>(null);
  const action = useAdminAction();
  const refresh = async () => {
    const [ops, server] = await Promise.all([api<Operations>("GET", "/api/admin/operations"), api<Overview>("GET", "/api/admin/overview")]);
    setOperations(ops); setOverview(server);
  };
  useEffect(() => { void action.run(refresh); }, []);
  return <div className="gm-console" data-testid="gm-console"><header className="gm-heading"><div><p className="gm-badge">{t("gm")}</p><h1>{t("title")}</h1></div><Button loading={action.pending} onClick={() => void action.run(refresh)}>{t("refresh")}</Button></header>
    <nav className="gm-tabs" aria-label={t("title")}>{(["operations", "accounts", "alliances", "events", "log"] as const).map((id) => <button key={id} type="button" aria-current={tab === id ? "page" : undefined} onClick={() => setTab(id)}>{t(id)}</button>)}</nav>
    {tab === "operations" && (operations && overview ? <OperationsPanel operations={operations} overview={overview} refresh={refresh} /> : <p role="status">{t("loading")}</p>)}
    {tab === "accounts" && <AccountsPanel />}{tab === "alliances" && <AlliancesPanel />}{tab === "events" && <LiveOpsPanel />}{tab === "log" && <AuditPanel />}
    {action.error && <p role="alert" className="gm-error">{action.error}</p>}
  </div>;
}

export function AdminView() {
  const t = useAdminText();
  const account = useSession((s) => s.account);
  return account?.role === "admin" ? <AuthorizedConsole key={account.id} /> : <section className="gm-console"><h1>{t("title")}</h1><p role="alert">{t("denied")}</p></section>;
}
